import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faXmark } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { guardianCheckFocusRef } from "@/services/api/guardiansv2";
import { type RuleCheckResult, type ScreeningDocument } from "@/services/api/guardiansv2/types";
import type { ScopeLabelCache } from "../audits/useScopeLabelCache";
import { EntityRichText } from "../shared/EntityRichText";
import { entityTextScopeFromAuditScope } from "../shared/entityTags";

const GUARDIAN_AGENT_NAME = "Ace";

function trimmed(value: string | null | undefined): string | null {
  const text = value?.trim();
  return text ? text : null;
}

function GuardianAvatar({ size = 18 }: { size?: number }) {
  return (
    <span
      className="inline-flex shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-rose-400 to-fuchsia-900 shadow-sm"
      style={{ width: size, height: size }}
      aria-hidden
    >
      <span
        className="block font-semibold leading-none text-white"
        style={{
          fontSize: size * 0.42,
          transform: `translate(${-size * 0.015}px, ${-size * 0.035}px)`,
        }}
      >
        A
      </span>
    </span>
  );
}

function StatusIcon({ check }: { check: RuleCheckResult }) {
  const passed = check.status === "passed";
  const ignored = check.status === "ignored";

  if (ignored) {
    return (
      <span className="text-[10px] font-bold text-slate-500" aria-hidden>
        —
      </span>
    );
  }

  if (passed) {
    return (
      <FontAwesomeIcon
        icon={faCircleCheck}
        className="text-[10px] text-slate-500"
        aria-hidden
      />
    );
  }

  return (
    <FontAwesomeIcon
      icon={faXmark}
      className="text-[10px] text-rose-300/70"
      aria-hidden
    />
  );
}

function GuardianCheckLine({
  check,
  entityScope,
  scopeLabels,
}: {
  check: RuleCheckResult;
  entityScope: ReturnType<typeof entityTextScopeFromAuditScope>;
  scopeLabels: ScopeLabelCache;
}) {
  const description = trimmed(check.description);

  return (
    <div
      className="flex items-start gap-2 text-sm leading-relaxed"
      data-testid={`screening-check-${check.rule_id}`}
      data-focus-ref={guardianCheckFocusRef(check.rule_id)}
    >
      <span className="flex h-[1.375rem] w-3 shrink-0 items-center justify-center">
        <StatusIcon check={check} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="font-medium text-slate-200">{check.title}</p>
        {description ? (
          <div className="mt-0.5 text-slate-100">
            <EntityRichText
              text={description}
              scope={entityScope}
              scopeLabels={scopeLabels}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

export interface GuardianEvalBubbleProps {
  screening: ScreeningDocument | null;
  scopeLabels: ScopeLabelCache;
}

/** Guardian screening bubble styled as an Ace agent message between task threads. */
export function GuardianEvalBubble({
  screening,
  scopeLabels,
}: GuardianEvalBubbleProps) {
  if (!screening) return null;

  const entityScope = entityTextScopeFromAuditScope(screening.scope ?? {});

  const { checks } = screening.checks;
  const failed = checks.filter((c) => c.status === "failed" || c.status === "error");
  const passed = checks.filter((c) => c.status === "passed");
  const ignored = checks.filter((c) => c.status === "ignored");
  const visible = [...failed, ...ignored, ...passed];

  if (visible.length === 0) return null;

  return (
    <div className="flex justify-start" data-testid="guardian-eval-bubble">
      <div className="max-w-[92%] min-w-0 overflow-hidden rounded-xl rounded-bl-md bg-fuchsia-500/[0.08] px-3 py-2 text-sm text-slate-200">
        <div className="flex items-center gap-1.5 text-[11px]">
          <GuardianAvatar />
          <span className="font-medium text-slate-300">{GUARDIAN_AGENT_NAME}</span>
        </div>
        <div className={clsx("mt-2 space-y-1", failed.length > 0 && "pt-0.5")}>
          {visible.map((check) => (
            <GuardianCheckLine
              key={`${check.kind}-${check.rule_id}`}
              check={check}
              entityScope={entityScope}
              scopeLabels={scopeLabels}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
